import {
  Issue,
  useCreateMutation,
  useIssue,
  useWorkspaceLabels,
} from '@gripp/shared-logic';
import {
  Colors,
  Button,
  Icon,
  IssueEdit,
  LoadingIndicator,
  MultiItemSelectorModal,
  Spacing,
  tokens,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';

type LabelItem = { id: string; name: string };

const CREATE_LABEL = `
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) {
      id
      name
    }
  }
`;

/**
 * Full-panel labels picker shown when the user taps the labels '+' in the
 * campaign thread settings. Mirrors the normal activity-edit labels picker
 * (MultiItemSelectorModal — search, "All" toggle, multi-select pills, inline
 * create). It is fully controlled by the parent so picks survive while the
 * IssueEdit form stays mounted (hidden) behind it.
 */
const CampaignThreadLabelsPicker = ({
  workspaceId,
  selectedLabels,
  onLabelsChange,
  onBack,
}: {
  workspaceId?: string;
  selectedLabels: LabelItem[];
  onLabelsChange: (labels: LabelItem[]) => void;
  onBack: () => void;
}) => {
  const { t } = useTranslation();
  const { labels: workspaceLabels = [] } = useWorkspaceLabels(workspaceId);
  const { mutateAsync: createLabel } = useCreateMutation({
    modelName: 'label',
    query: CREATE_LABEL,
  });

  const [search, setSearch] = useState('');
  const [createdLabels, setCreatedLabels] = useState<LabelItem[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const allLabels = useMemo(() => {
    const map = new Map<string, LabelItem>();
    [...workspaceLabels, ...createdLabels].forEach((label) => {
      if (label?.id) map.set(label.id, { id: label.id, name: label.name });
    });
    return Array.from(map.values());
  }, [workspaceLabels, createdLabels]);

  const selectedIds = useMemo(
    () => new Set(selectedLabels.map((l) => l.id)),
    [selectedLabels],
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const base = query
      ? allLabels.filter((l) => l.name.toLowerCase().includes(query))
      : allLabels;
    return base.map((l) => ({ ...l, isActive: selectedIds.has(l.id) }));
  }, [allLabels, search, selectedIds]);

  const canCreateItem =
    !!search.trim() &&
    !allLabels.some(
      (l) => l.name.toLowerCase() === search.trim().toLowerCase(),
    );

  const handleItemToggle = (item: LabelItem) => {
    if (selectedIds.has(item.id)) {
      onLabelsChange(selectedLabels.filter((l) => l.id !== item.id));
      return;
    }
    onLabelsChange([...selectedLabels, { id: item.id, name: item.name }]);
  };

  const handleToggleAll = (selectAll: boolean) => {
    if (selectAll) {
      const toAdd = filteredItems
        .filter((i) => !selectedIds.has(i.id))
        .map((i) => ({ id: i.id, name: i.name }));
      onLabelsChange([...selectedLabels, ...toAdd]);
      return;
    }
    const ids = new Set(filteredItems.map((i) => i.id));
    onLabelsChange(selectedLabels.filter((l) => !ids.has(l.id)));
  };

  const handleCreateLabel = async (name: string): Promise<LabelItem> =>
    (await createLabel({
      input: { name, workspace: { id: workspaceId } },
    })) as LabelItem;

  const handleCreatePress = async () => {
    if (!search.trim() || !workspaceId) return;
    setIsCreating(true);
    try {
      const created = await handleCreateLabel(search.trim());
      setCreatedLabels((prev) => [...prev, created]);
      onLabelsChange([...selectedLabels, created]);
      setSearch('');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <View style={styles.pickerContainer}>
      <Pressable
        onPress={onBack}
        accessibilityRole="button"
        accessibilityLabel={t('common.back', 'Back')}
        style={styles.pickerBack}
        testID="campaign-labels-picker-back"
      >
        <Icon name="Chevron Left" size={20} color={Colors.role.textStrong} />
        <Text style={styles.pickerBackText}>
          {`${t('labels.labels', 'Labels')} (${selectedLabels.length})`}
        </Text>
      </Pressable>
      <MultiItemSelectorModal
        showOptions
        search={search}
        setSearch={setSearch}
        searchPlaceholder={t('common.searchOrCreate', 'Search or create')}
        filteredItems={filteredItems}
        onItemToggle={handleItemToggle}
        onToggleAll={handleToggleAll}
        canCreateItem={canCreateItem}
        onCreateItem={handleCreateLabel}
        onCreatePress={handleCreatePress}
        isCreating={isCreating}
        createButtonText={t('common.create', 'Create')}
      />
    </View>
  );
};

/**
 * CampaignThreadEditPanel
 *
 * Edit affordance for a campaign thread's underlying Issue. Wraps `IssueEdit`,
 * which renders read-only for `issue.type === IssueType.Campaign` — it hides
 * type / reassign / adjust-creation-date and disables title. Pin, reminder and
 * labels are kept editable here (`allowCampaignMetadataEdit`) and saved through
 * the standard issue-edit save button (same `contentFooter` pattern as
 * ActivityEditColumn), minus delete.
 *
 * Labels open the normal full-panel picker. The campaigns SlideLayout already
 * uses all four columns when this panel is open, so instead of sliding in a
 * fifth column the picker is shown as an in-panel sub-view: IssueEdit stays
 * mounted (hidden) so label picks persist, and label state is lifted here and
 * driven into IssueEdit as `externalLabels`.
 */
export const CampaignThreadEditPanel = ({ issueId }: { issueId: string }) => {
  const { t } = useTranslation();
  const { issue: issueData, isLoading } = useIssue(issueId);
  const issue = issueData as unknown as Issue | undefined;

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const onSaveRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const [showLabelsPicker, setShowLabelsPicker] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<LabelItem[]>([]);
  const [labelsSeeded, setLabelsSeeded] = useState(false);

  // Seed the lifted label state once the issue loads. Until then we leave
  // `externalLabels` undefined so IssueEdit falls back to `currentLabels`
  // rather than being driven to an empty array on the first render.
  useEffect(() => {
    if (issue && !labelsSeeded) {
      setSelectedLabels(issue.labels ?? []);
      setLabelsSeeded(true);
    }
  }, [issue, labelsSeeded]);

  const handleFormStateChange = useCallback(
    (isDirty: boolean, isValid: boolean, onSave: () => Promise<void>) => {
      setFormIsDirty(isDirty);
      setFormIsValid(isValid);
      onSaveRef.current = onSave;
    },
    [],
  );

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!issue) {
    return (
      <View style={styles.centeredMessage}>
        <Text style={styles.centeredMessageText}>
          {t('campaigns.thread.notFound', 'Thread not found')}
        </Text>
      </View>
    );
  }

  const issueCreatedAt =
    issue.createdAt instanceof Date
      ? issue.createdAt.toISOString()
      : issue.createdAt;

  const canSave = formIsDirty && formIsValid;

  return (
    <View style={styles.root}>
      <View style={[styles.fill, showLabelsPicker && styles.hidden]}>
        <IssueEdit
          issue={issue}
          issueId={issue.id}
          currentTitle={issue.title}
          workspaceId={issue.workspace?.id}
          currentLabels={issue.labels}
          externalLabels={labelsSeeded ? selectedLabels : undefined}
          onLabelsChange={setSelectedLabels}
          onLabelSelectorEdit={() => setShowLabelsPicker(true)}
          currentAdjustCreationDate={issue.adjustCreationDate}
          currentCreatedAt={issueCreatedAt}
          allowCampaignMetadataEdit
          bottomBar={() => null}
          onFormStateChange={handleFormStateChange}
          contentFooter={
            <View style={styles.contentActions}>
              <Button
                style={styles.saveButton}
                onPress={canSave ? () => onSaveRef.current() : undefined}
                state={canSave ? 'active' : 'default'}
                disabled={!canSave}
              >
                {t('issue.editModal.save')}
              </Button>
            </View>
          }
        />
      </View>
      {showLabelsPicker && (
        <CampaignThreadLabelsPicker
          workspaceId={issue.workspace?.id}
          selectedLabels={selectedLabels}
          onLabelsChange={setSelectedLabels}
          onBack={() => setShowLabelsPicker(false)}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  fill: {
    flex: 1,
  },
  hidden: {
    display: 'none',
  },
  centeredMessage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing['2xl'],
  },
  centeredMessageText: {
    ...tokens.typography.body.small,
    color: Colors.role.textSecondary,
  },
  contentActions: {
    paddingHorizontal: Spacing.baseMargin.base,
    paddingTop: Spacing.baseMargin.base,
    gap: Spacing.baseMargin.base / 4,
  },
  saveButton: {
    width: '100%',
    height: 48,
    borderRadius: 2,
  },
  pickerContainer: {
    flex: 1,
    backgroundColor: Colors.role.canvas,
  },
  pickerBack: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.md,
  },
  pickerBackText: {
    ...tokens.typography.body.smallEmphasised,
    color: Colors.role.textStrong,
  },
});
